import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Frame, Toast } from '@shopify/polaris'
import HomePage from './pages/HomePage'
import SettingsPageSimple from './pages/SettingsPageSimple'
import SettingsPage from './pages/SettingsPage'
import AnalyticsPage from './pages/AnalyticsPage'
import LandingPage from './pages/LandingPage'
import TestingPage from './pages/TestingPage'
import EmailSetupGuidePage from './pages/EmailSetupGuidePage'
import EmailTemplateEditorPage from './pages/EmailTemplateEditorPage'
import ChatPage from './pages/ChatPage'
import ChatSettingsPage from './pages/ChatSettingsPage'
import BundlesPage from './pages/BundlesPage'
import DiscountCodesPage from './pages/DiscountCodesPage'
import PricingPage from './pages/PricingPage'
import ABTestPage from './pages/ABTestPage'
import SalesStrategyPage from './pages/SalesStrategyPage'
import AboutPage from './pages/legal/AboutPage'
import PrivacyPage from './pages/legal/PrivacyPage'
import TermsPage from './pages/legal/TermsPage'
import GDPRPage from './pages/legal/GDPRPage'
import CookiesPage from './pages/legal/CookiesPage'
import DocsPage from './pages/legal/DocsPage'
import { ShopProvider } from './context/ShopContext'
import { OperationProgressProvider, useOperationProgressContext } from './context/OperationProgressContext'
import { useEmbedded } from './components/ShopifyAppProvider'

// Component to sync Shopify locale with i18next
function LocaleSync() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Get locale from URL parameters (Shopify passes this)
    const params = new URLSearchParams(window.location.search);
    const shopifyLocale = params.get('locale');
    
    if (shopifyLocale && shopifyLocale !== i18n.language) {
      // Change i18next language to match Shopify's locale
      i18n.changeLanguage(shopifyLocale);
    }
  }, [i18n]);
  
  return null;
}

// Toast component that reads from context - must be inside Frame AND OperationProgressProvider
function ToastManager() {
  const { toast, dismissToast } = useOperationProgressContext();
  
  if (!toast) return null;
  
  return (
    <Toast
      content={toast.content}
      error={toast.error}
      onDismiss={dismissToast}
      duration={toast.error ? 5000 : 3000}
    />
  );
}

// Admin routes that render inside Polaris Frame
function AdminRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/app" element={<HomePage />} />
      <Route path="/app/settings" element={<SettingsPageSimple />} />
      <Route path="/app/settings/advanced" element={<SettingsPage />} />
      <Route path="/app/email" element={<EmailSetupGuidePage />} />
      <Route path="/app/email/template/:templateType" element={<EmailTemplateEditorPage />} />
      <Route path="/app/analytics" element={<AnalyticsPage />} />
      <Route path="/app/testing" element={<TestingPage />} />
      <Route path="/app/chat" element={<ChatPage />} />
      <Route path="/app/chat/settings" element={<ChatSettingsPage />} />
      <Route path="/app/chat/discounts" element={<DiscountCodesPage />} />
      <Route path="/app/bundles" element={<BundlesPage />} />
      <Route path="/app/ab-tests" element={<ABTestPage />} />
      <Route path="/app/strategy" element={<SalesStrategyPage />} />
      <Route path="/app/pricing" element={<PricingPage />} />
    </Routes>
  );
}

// Inner app content with Frame - provides Polaris layout, Toast, etc.
function FramedContent(): JSX.Element {
  return (
    <Frame>
      <OperationProgressProvider>
        <LocaleSync />
        <AdminRoutes />
        <ToastManager />
      </OperationProgressProvider>
    </Frame>
  );
}

// Main app router - public pages render WITHOUT Frame, admin pages WITH Frame
function AppRouter(): JSX.Element {
  const { isEmbedded } = useEmbedded();

  return (
    <Routes>
      {/* LandingPage at root when NOT embedded - renders outside Frame */}
      {!isEmbedded && <Route path="/" element={<><LocaleSync /><LandingPage /></>} />}

      {/* Public legal/info pages - render outside Frame */}
      <Route path="/about" element={<><LocaleSync /><AboutPage /></>} />
      <Route path="/privacy" element={<><LocaleSync /><PrivacyPage /></>} />
      <Route path="/terms" element={<><LocaleSync /><TermsPage /></>} />
      <Route path="/gdpr" element={<><LocaleSync /><GDPRPage /></>} />
      <Route path="/cookies" element={<><LocaleSync /><CookiesPage /></>} />
      <Route path="/docs" element={<><LocaleSync /><DocsPage /></>} />

      {/* All other routes - render inside Frame for Polaris admin layout */}
      <Route path="*" element={<FramedContent />} />
    </Routes>
  );
}

function App(): JSX.Element {
  return (
    <BrowserRouter>
      <ShopProvider>
        <AppRouter />
      </ShopProvider>
    </BrowserRouter>
  )
}

export default App
